import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { TiptapEditor } from './TiptapEditor';
import { ArrowLeft, Eye, EyeSlash, Image as ImageIcon } from '@phosphor-icons/react';

const monoFont = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.15)',
  borderStrong: 'rgba(247,246,242,0.25)',
  bgHover: 'rgba(247,246,242,0.06)',
  published: '#00E87A',
};

interface FreePage {
  id: string;
  title: string;
  slug: string;
  content_json: any;
  published: boolean;
}

interface AdminFreePageEditProps {
  pageId: string;
  onBack: () => void;
}

export function AdminFreePageEdit({ pageId, onBack }: AdminFreePageEditProps) {
  const { session } = useAuth();
  const [page, setPage] = useState<FreePage | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('free_pages')
        .select('*')
        .eq('id', pageId)
        .single();
      if (data) {
        const p = data as unknown as FreePage;
        setPage(p);
        setTitle(p.title);
        setContent(p.content_json);
      }
    };
    load();
  }, [pageId]);

  const handleSave = async () => {
    if (!page) return;
    setSaving(true);
    await supabase.from('free_pages').update({
      title,
      content_json: content,
    } as any).eq('id', page.id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const togglePublish = async () => {
    if (!page) return;
    const newPublished = !page.published;
    await supabase.from('free_pages').update({ published: newPublished } as any).eq('id', page.id);
    setPage({ ...page, published: newPublished });
  };

  const handleImageUpload = async (file: File) => {
    if (!session || !page) return null;
    const ext = file.name.split('.').pop();
    const path = `${session.user.id}/pages/${page.slug}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('site-assets').upload(path, file, { upsert: true });
    if (error) return null;
    const { data: urlData } = supabase.storage.from('site-assets').getPublicUrl(path);
    return urlData?.publicUrl || null;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await handleImageUpload(file);
    if (url) {
      // Content will be updated via TiptapEditor's onChange
      setContent((prev: any) => {
        // We'll insert the image via a prompt workaround — set it in state
        return prev;
      });
      // Use prompt approach by inserting via URL
      window.dispatchEvent(new CustomEvent('tiptap-insert-image', { detail: { url } }));
    }
    e.target.value = '';
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 14, fontWeight: 300, padding: '10px 14px',
    border: `1px solid ${C.border}`, backgroundColor: 'rgba(247,246,242,0.04)', color: C.fg, width: '100%',
  };

  if (!page) {
    return <p style={{ fontFamily: monoFont, color: C.fgMuted }}>Carregando...</p>;
  }

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          style={{ color: C.fgMuted, background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <ArrowLeft size={18} weight="thin" />
        </button>
        <h2 className="uppercase text-sm font-light tracking-wider flex-1" style={{ fontFamily: monoFont, color: C.fg }}>
          EDITAR PÁGINA
        </h2>
        <button
          onClick={togglePublish}
          className="text-[11px] uppercase inline-flex items-center gap-1.5 py-1 px-3"
          style={{
            fontFamily: monoFont,
            border: '0.5px solid',
            borderColor: page.published ? C.published : C.border,
            color: page.published ? C.published : C.fgMuted,
            backgroundColor: 'transparent',
            cursor: 'pointer',
          }}
        >
          {page.published ? <><Eye size={12} weight="thin" /> Publicada</> : <><EyeSlash size={12} weight="thin" /> Rascunho</>}
        </button>
      </div>

      {/* Title */}
      <div className="flex flex-col gap-1 mb-5">
        <label className="text-[10px] uppercase tracking-wider" style={{ fontFamily: monoFont, color: C.fgMuted }}>Título</label>
        <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />
      </div>

      {/* Slug (read-only) */}
      <div className="flex flex-col gap-1 mb-5">
        <label className="text-[10px] uppercase tracking-wider" style={{ fontFamily: monoFont, color: C.fgMuted }}>Slug</label>
        <p className="text-xs" style={{ fontFamily: monoFont, color: C.fgMuted }}>{page.slug}</p>
      </div>

      {/* Content editor */}
      <div className="flex flex-col gap-1 mb-5">
        <label className="text-[10px] uppercase tracking-wider" style={{ fontFamily: monoFont, color: C.fgMuted }}>Conteúdo</label>
        <TiptapEditor
          content={content}
          onChange={setContent}
          onImageUpload={() => fileInputRef.current?.click()}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="uppercase text-xs py-2.5 px-5"
          style={{
            fontFamily: monoFont,
            color: '#0A0A0A',
            backgroundColor: C.fg,
            border: 'none',
            cursor: saving ? 'wait' : 'pointer',
          }}
        >
          {saving ? 'SALVANDO...' : 'SALVAR'}
        </button>
        {saved && (
          <span className="text-[11px]" style={{ fontFamily: monoFont, color: C.published }}>✓ Salvo</span>
        )}
      </div>
    </div>
  );
}
